import type { MessengerMessageSearchResult } from '@/api/client';
import { MessengerMutedStatus } from '@/components/Messenger/shared/chrome/MessengerMutedStatus';
import { buildMessengerMessagePreviewLine } from '@/lib/messengerNotificationPreview';
import {
  highlightPlainText,
  SEARCH_HIGHLIGHT_CLASS_MESSENGER,
} from '@/utils/highlightSearchTerms';

interface ChatSidebarMessageSearchResultsProps {
  results: MessengerMessageSearchResult[];
  loading: boolean;
  query: string;
  highlightWords: string[];
  onSelect: (result: MessengerMessageSearchResult) => void;
  /** Одна строка/кнопки без секции «Сообщения» — для общего списка поиска */
  variant?: 'section' | 'embedded';
}

function getSearchResultTitle(result: MessengerMessageSearchResult): string {
  if (result.is_saved) return 'Сохранённые сообщения';
  if (result.thread_kind === 'group') return result.title?.trim() || 'Группа';
  return result.peer_name?.trim() || 'Чат';
}

function getSearchResultText(result: MessengerMessageSearchResult): string {
  return buildMessengerMessagePreviewLine(result.message);
}

function messageResultButtons(
  results: MessengerMessageSearchResult[],
  highlightWords: string[],
  onSelect: (result: MessengerMessageSearchResult) => void
) {
  const hlTitle = (s: string) =>
    highlightWords.length > 0
      ? highlightPlainText(s, highlightWords, SEARCH_HIGHLIGHT_CLASS_MESSENGER)
      : s;
  const hlText = (s: string) =>
    highlightWords.length > 0
      ? highlightPlainText(s, highlightWords, SEARCH_HIGHLIGHT_CLASS_MESSENGER)
      : s;

  return results.map((result) => (
    <button
      key={result.message.id}
      type="button"
      className="chatSidebar__item chatSidebar__messageResult"
      onClick={() => onSelect(result)}
    >
      <span className="chatSidebar__itemContent">
        <span className="chatSidebar__itemLabelRow">
          <span className="chatSidebar__itemLabel">{hlTitle(getSearchResultTitle(result))}</span>
        </span>
        <span className="chatSidebar__itemSubtitle">{hlText(getSearchResultText(result))}</span>
      </span>
    </button>
  ));
}

export function ChatSidebarMessageSearchResults({
  results,
  loading,
  query,
  highlightWords,
  onSelect,
  variant = 'section',
}: ChatSidebarMessageSearchResultsProps) {
  if (query.trim().length < 2) return null;
  if (!loading && results.length === 0) return null;

  if (variant === 'embedded') {
    if (loading) {
      return (
        <MessengerMutedStatus as="div">
          <span className="chatSidebar__searchSpinner" aria-hidden />
          <span>Поиск…</span>
        </MessengerMutedStatus>
      );
    }
    return <>{messageResultButtons(results, highlightWords, onSelect)}</>;
  }

  return (
    <section className="chatSidebar__section" aria-labelledby="chatSidebar-section-messages">
      <h2 className="chatSidebar__groupLabel chatSidebar__groupLabel_section" id="chatSidebar-section-messages">
        Сообщения
      </h2>
      {loading ? (
        <MessengerMutedStatus as="div">
          <span className="chatSidebar__searchSpinner" aria-hidden />
          <span>Ищем сообщения</span>
        </MessengerMutedStatus>
      ) : (
        <div className="chatSidebar__itemStack">{messageResultButtons(results, highlightWords, onSelect)}</div>
      )}
    </section>
  );
}
